import {
  Box,
  Heading,
  Text,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  UnorderedList,
  OrderedList,
  ListItem,
} from '@chakra-ui/react'
import { StepContent } from './HelpStepContent'

const H = (file: string) => `/assets/img/help/${file}`

type Props = {
  border: string
  textSecondary: string
}

/** Service Catalog group — lists, forms, and workflows. */
export function ServiceCatalogManual({ border, textSecondary }: Props) {
  return (
    <Box>
      <Divider borderColor={border} mb={6} />
      <Heading size="md" mb={2}>
        Service Catalog
      </Heading>
      <Text color={textSecondary} mb={4}>
        Configure reference data used across Tickets and Tasks. Open the sidebar group{' '}
        <strong>Service Catalog</strong>. Page titles sometimes differ from menu labels (noted below). Most lists share
        the same trash / restore pattern.
      </Text>

      <Accordion allowMultiple defaultIndex={[0]}>
        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                1. Shared list workflow (All / Trash)
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshot={{
                src: H('sc-01-service-types-list.png'),
                alt: 'Service Catalog list with All and Trash tabs',
                caption: 'Typical catalog list: All / Trash, search, Create, bulk actions, row Edit / Trash.',
              }}
            >
              <Text mb={2} fontSize="sm">
                Applies to Service Types, Parent Ticket Statuses, Task Statuses, SLA Policies, Task Templates, Issue
                Categories, Form Option values, Tool / Part Catalog (and similar soft-delete tables).
              </Text>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  Tabs: <strong>All</strong> (count badge) · <strong>Trash</strong> (red count).
                </ListItem>
                <ListItem>
                  Search: type then Enter or the search icon. Placeholders vary (e.g. <em>Search by name, code...</em>).
                </ListItem>
                <ListItem>
                  Bulk: select rows → <strong>Bulk actions</strong> → All: <strong>Move to trash</strong> · Trash:{' '}
                  <strong>Restore</strong> / <strong>Delete Permanently</strong> → <strong>Apply</strong> → confirm.
                </ListItem>
                <ListItem>
                  Row: <strong>Edit</strong> · <strong>Trash</strong> (All) · Trash tab: <strong>Restore</strong> ·{' '}
                  <strong>Delete Permanently</strong>.
                </ListItem>
                <ListItem>
                  Create: primary button (label differs per page) opens a create page or modal.
                </ListItem>
              </UnorderedList>
              <Text fontSize="sm" color={textSecondary}>
                Exceptions: CSAT Surveys (soft-delete, no bulk bar), Checklist Templates &amp; FE Checklist Builder (hard
                delete, no All/Trash), Task Field Modules (Custom / Reserved tabs instead of trash).
              </Text>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                2. Service Types
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('sc-01-service-types-list.png'),
                  alt: 'Service Types list',
                  caption: 'Service Types list — Name, Code, Description, Status, Parent, Updated At.',
                },
                {
                  src: H('sc-02-service-types-create.png'),
                  alt: 'Create Service Type form',
                  caption: 'Create Service Type: Name*, Code*, Description, Parent, Active.',
                },
              ]}
            >
              <Text mb={2} fontSize="sm">
                Menu: <strong>Service Types</strong> · Route{' '}
                <Text as="span" fontFamily="mono" fontSize="sm">
                  /service-catalog/service-types
                </Text>
              </Text>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  Create: <strong>Create Service Type</strong> → pages Create / Edit Service Type.
                </ListItem>
                <ListItem>
                  Fields: <strong>Name</strong>* (<em>e.g. Hardware Support</em>) · <strong>Code</strong>* (
                  <em>e.g. HW-SUPPORT</em>; locked after create) · <strong>Description</strong> ·{' '}
                  <strong>Parent (category)</strong> (None = top-level) · <strong>Active</strong>.
                </ListItem>
                <ListItem>
                  Footer: <strong>Cancel</strong> · <strong>Create Service Type</strong> / <strong>Save</strong>.
                </ListItem>
              </UnorderedList>
              <Text fontSize="sm" color={textSecondary}>
                Used on ticket requests / service type dropdowns and related catalog filters.
              </Text>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                3. Parent Ticket Statuses
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('sc-03-parent-statuses.png'),
                  alt: 'Ticket Statuses list',
                  caption: 'Page title Ticket Statuses — Code, Label, Closed, On hold.',
                },
                {
                  src: H('sc-03b-parent-status-create.png'),
                  alt: 'Create Ticket Status form',
                  caption: 'Create Ticket Status: Code*, Label, Closed, On hold.',
                },
              ]}
            >
              <Text mb={2} fontSize="sm">
                Menu: <strong>Parent Ticket Statuses</strong> · Page heading: <strong>Ticket Statuses</strong>
              </Text>
              <UnorderedList spacing={2} pl={1}>
                <ListItem>
                  Create: <strong>Create Ticket Status</strong>.
                </ListItem>
                <ListItem>
                  Fields: <strong>Code</strong>* (locked on edit) · <strong>Label</strong> · <strong>Closed</strong>{' '}
                  (“This status is a closed state.”) · <strong>On hold</strong> (“This status puts the ticket on
                  hold.”).
                </ListItem>
                <ListItem>
                  Footer: Cancel · Create Ticket Status / Save.
                </ListItem>
              </UnorderedList>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                4. Task Statuses
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('sc-04-task-statuses.png'),
                  alt: 'Task Statuses list',
                  caption: 'Task Statuses — Code, Label, Color, Order, SLA, Auto pause, Scope, Terminal, Triggers parent.',
                },
                {
                  src: H('sc-05-task-status-create.png'),
                  alt: 'Create Task Status form',
                  caption: 'Create Task Status form with scope, color, terminal, and parent trigger.',
                },
              ]}
            >
              <Text mb={2} fontSize="sm">
                Menu / page: <strong>Task Statuses</strong> · Route{' '}
                <Text as="span" fontFamily="mono" fontSize="sm">
                  /service-catalog/child-ticket-statuses
                </Text>
              </Text>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  Extra row actions (All): <strong>Move up</strong> · <strong>Move down</strong> (sort order).
                </ListItem>
                <ListItem>
                  Create: <strong>Create Task Status</strong>.
                </ListItem>
                <ListItem>
                  Fields: <strong>Code</strong>* · <strong>Label</strong> · <strong>Default SLA (minutes)</strong> ·{' '}
                  <strong>Triggers parent status</strong> · <strong>Scope</strong> (Generic / FE Visit / Parts / Permit /
                  Custom) · <strong>Color</strong> · <strong>Sort order</strong> · <strong>Auto pause</strong> ·{' '}
                  <strong>Is terminal</strong> (may lock when in use).
                </ListItem>
              </UnorderedList>
              <Text fontSize="sm" color={textSecondary}>
                Drives task drawer status dropdowns and template default statuses.
              </Text>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                5. SLA Policies
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('sc-06-sla-list.png'),
                  alt: 'SLA and Timing list',
                  caption: 'SLA & Timing — Applies to, Priority, Response/Resolution minutes, pause flags.',
                },
                {
                  src: H('sc-07-sla-create.png'),
                  alt: 'Create SLA form',
                  caption: 'Create SLA: Applies to*, Priority, Response, Resolution, Pause on hold / FE visit.',
                },
              ]}
            >
              <Text mb={2} fontSize="sm">
                Menu: <strong>SLA Policies</strong> · Page: <strong>SLA &amp; Timing</strong>
              </Text>
              <UnorderedList spacing={2} pl={1}>
                <ListItem>
                  Create: <strong>Create SLA</strong>.
                </ListItem>
                <ListItem>
                  Fields: <strong>Applies to</strong>* (Ticket / Child ticket from Form Options{' '}
                  <code>sla_applies_to</code>) · <strong>Priority</strong> · <strong>Response (minutes)</strong> ·{' '}
                  <strong>Resolution (minutes)</strong> · <strong>Pause on on-hold</strong> ·{' '}
                  <strong>Pause on FE visit</strong>.
                </ListItem>
                <ListItem>Footer: Cancel · Create SLA / Save.</ListItem>
              </UnorderedList>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                6. CSAT Surveys
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('sc-08-csat-list.png'),
                  alt: 'CSAT surveys list',
                  caption: 'Customer Satisfaction Surveys — Survey Name, Active, Description, Updated.',
                },
                {
                  src: H('sc-09-csat-modal.png'),
                  alt: 'New Survey modal',
                  caption: 'New Survey modal: name, description, active, questions (Rating / Free text).',
                },
              ]}
            >
              <Text mb={2} fontSize="sm">
                Menu: <strong>CSAT Surveys</strong> · Page: <strong>Customer Satisfaction Surveys</strong> · Modal CRUD
                (no separate create route).
              </Text>
              <UnorderedList spacing={2} pl={1}>
                <ListItem>
                  Create: <strong>New Survey</strong>. Soft-delete via row Trash (no bulk bar).
                </ListItem>
                <ListItem>
                  Fields: <strong>Survey Name</strong>* · <strong>Description</strong> ·{' '}
                  <strong>Active (used for new invitations)</strong>.
                </ListItem>
                <ListItem>
                  Questions: <strong>Add Question</strong> — Question text, type <strong>Rating (1–5)</strong> /{' '}
                  <strong>Free text</strong>, Required; cannot remove the last question.
                </ListItem>
                <ListItem>
                  Footer: Cancel · <strong>Save Survey</strong>.
                </ListItem>
              </UnorderedList>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                7. Task Templates
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('sc-10-task-templates.png'),
                  alt: 'Task Templates list',
                  caption: 'Task Templates — Name, Description, Support type, Steps, Sequential, Active.',
                },
                {
                  src: H('sc-11-task-template-create.png'),
                  alt: 'Create Task Template form',
                  caption:
                    'Create template: name/code, resolution defaults, Add step, field bundles, checklists (scroll for full form).',
                },
              ]}
            >
              <Text mb={2} fontSize="sm">
                Menu: <strong>Task Templates</strong> · Used by Tickets → Tasks → Create task / Apply template.
              </Text>
              <Heading as="h4" size="sm">
                Process
              </Heading>
              <OrderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  Create a <em>standalone</em> template (no multi-step workflow) for “Create task → From template”.
                </ListItem>
                <ListItem>
                  Create a <em>sequential/parallel workflow</em> (multiple steps) for “Apply template” on a ticket.
                </ListItem>
                <ListItem>
                  Optionally attach Checklist Templates and Task Field Modules to steps.
                </ListItem>
              </OrderedList>
              <Heading as="h4" size="sm">
                Form fields
              </Heading>
              <UnorderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  Top: <strong>Name</strong>* · <strong>Code</strong> · <strong>Description</strong> ·{' '}
                  <strong>Support type</strong> · <strong>Sequential workflow</strong> · <strong>Active</strong>.
                </ListItem>
                <ListItem>
                  Resolution defaults: Title / Notes patterns · Default assignee / priority / due date rules · Allowed
                  creator roles · Task field module · Default status.
                </ListItem>
                <ListItem>
                  Steps: <strong>Add step</strong> — Title* · Status* · SLA (min) · Depends on · Checklist · Field
                  bundles (<strong>+ FE bundle</strong>, <strong>+ Assets</strong>, <strong>+ Parts</strong>,{' '}
                  <strong>+ Permit</strong>) · <strong>Add field</strong> from module palette (label, Required).
                </ListItem>
                <ListItem>
                  Standalone checklist section + link <strong>Manage checklist templates</strong>.
                </ListItem>
                <ListItem>
                  Footer: Cancel · <strong>Create template</strong> / <strong>Save template</strong>.
                </ListItem>
              </UnorderedList>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                8. Task Field Modules
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('sc-12-field-modules.png'),
                  alt: 'Task Field Modules Custom tab',
                  caption: 'Task Field Modules — Custom / Reserved tabs; Add Custom Module.',
                },
                {
                  src: H('sc-12b-field-module-modal.png'),
                  alt: 'Add Custom Module modal',
                  caption: 'Add Custom Module: Module key*, Label*, Kind, Scope, Category, URL template, Active.',
                },
              ]}
            >
              <UnorderedList spacing={2} pl={1}>
                <ListItem>
                  Tabs: <strong>Custom</strong> · <strong>Reserved</strong> (built-in, read-only).
                </ListItem>
                <ListItem>
                  Custom: <strong>Add Custom Module</strong> — Module key* (saved as <code>custom.{'{key}'}</code>) ·
                  Label* · Kind (iframe / external link) · Scope (Task / Ticket) · Category · URL template · Active.
                </ListItem>
                <ListItem>
                  Row: Edit · <strong>Delete</strong> (custom only). Reserved has no actions.
                </ListItem>
                <ListItem>
                  Modules appear in the Task Template field palette and task drawer Details.
                </ListItem>
              </UnorderedList>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                9. Checklist Templates
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('sc-13-checklist-templates.png'),
                  alt: 'Checklist Templates list',
                  caption: 'Checklist Templates — Name, Items, Status. Create checklist.',
                },
                {
                  src: H('sc-13b-checklist-template-modal.png'),
                  alt: 'Create checklist modal',
                  caption: 'Create checklist: Name*, Active, Items (label, Required), Add item.',
                },
              ]}
            >
              <UnorderedList spacing={2} pl={1}>
                <ListItem>
                  No All/Trash — hard delete from the table when permitted.
                </ListItem>
                <ListItem>
                  <strong>Create checklist</strong> / Edit modal: <strong>Name</strong>* · <strong>Active</strong> ·
                  Items (label, Required, remove) · <strong>Add item</strong> · Cancel / <strong>Save</strong>.
                </ListItem>
                <ListItem>
                  Attached from Task Templates (step or standalone) and from a task Checklist tab (“Attach checklist…”).
                </ListItem>
              </UnorderedList>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                10. Issue Categories
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('sc-14-issue-categories.png'),
                  alt: 'Issue Categories list',
                  caption: 'Issue Categories — cascade parent/child for ticket Issue classification.',
                },
                {
                  src: H('sc-14b-issue-category-create.png'),
                  alt: 'Create Issue Category form',
                  caption: 'Create Issue Category: Name*, Code, Parent category, Description, Active.',
                },
              ]}
            >
              <UnorderedList spacing={2} pl={1}>
                <ListItem>
                  Create: <strong>Create Issue Category</strong>.
                </ListItem>
                <ListItem>
                  Fields: <strong>Name</strong>* · <strong>Code</strong> · <strong>Parent category</strong> (builds
                  category → sub-category → item) · <strong>Description</strong> · <strong>Active</strong>.
                </ListItem>
                <ListItem>
                  Powers ticket form <strong>Issue classification</strong> cascade.
                </ListItem>
              </UnorderedList>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                11. Form Options (Ticket Option Lists)
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('sc-15-form-options.png'),
                  alt: 'Ticket Option Lists',
                  caption: 'Select option list + values grid (Code, Label, Sort, Status).',
                },
                {
                  src: H('sc-15b-form-options-new-list.png'),
                  alt: 'New Option List modal',
                  caption: 'New Option List: Display name*, Group code* (locked later), Description.',
                },
                {
                  src: H('sc-16-form-options-add-value.png'),
                  alt: 'Add Value modal',
                  caption: 'Add Value: Code*, Label*, Sort order, Active.',
                },
              ]}
            >
              <Text mb={2} fontSize="sm">
                Menu: <strong>Form Options</strong> · Page: <strong>Ticket Option Lists</strong>
              </Text>
              <OrderedList spacing={2} pl={1} mb={3}>
                <ListItem>
                  Pick a group from <strong>Select option list</strong> (or <strong>New Option List</strong>).
                </ListItem>
                <ListItem>
                  Manage values under All / Trash · <strong>Add Value</strong>.
                </ListItem>
              </OrderedList>
              <UnorderedList spacing={2} pl={1}>
                <ListItem>
                  New list: <strong>Display name</strong>* · <strong>Group code</strong>* (lowercase; cannot change
                  later) · Description.
                </ListItem>
                <ListItem>
                  Value: <strong>Code</strong>* (stable submitted value) · <strong>Label</strong>* ·{' '}
                  <strong>Sort order</strong> · <strong>Active</strong>.
                </ListItem>
                <ListItem>
                  Seeded groups include Severity, Billing Type, Ticket Source, Onsite hold reason, SLA Applies To, FE
                  Role, and more.
                </ListItem>
              </UnorderedList>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                12. Tool / Part Catalog
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('sc-17-tool-part.png'),
                  alt: 'Tool Part Catalog list',
                  caption: 'Tool / Part Catalog — Name, Usage, Status, Updated. Add Tool / Part.',
                },
                {
                  src: H('sc-17b-tool-part-modal.png'),
                  alt: 'Add Tool Part modal',
                  caption: 'Add Tool / Part: Name*, Active (included in autocomplete).',
                },
              ]}
            >
              <UnorderedList spacing={2} pl={1}>
                <ListItem>
                  Create: <strong>Add Tool / Part</strong> (modal).
                </ListItem>
                <ListItem>
                  Fields: <strong>Name</strong>* (autocomplete / job card) · <strong>Active</strong> (included in
                  autocomplete).
                </ListItem>
                <ListItem>
                  Footer: Cancel · <strong>Add</strong> / <strong>Save</strong>.
                </ListItem>
                <ListItem>
                  Feeds ticket <strong>Tools / parts needed for this job</strong> name suggestions.
                </ListItem>
              </UnorderedList>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                13. FE Checklist Builder
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent
              border={border}
              textSecondary={textSecondary}
              screenshots={[
                {
                  src: H('sc-18-fe-checklists.png'),
                  alt: 'FE Checklist Builder',
                  caption: 'FE Checklist Builder — Name, Items, Status.',
                },
                {
                  src: H('sc-18b-fe-checklist-modal.png'),
                  alt: 'Create FE Checklist modal',
                  caption: 'Create Checklist: Name*, Active, Items (label, Required), Add item.',
                },
              ]}
            >
              <UnorderedList spacing={2} pl={1}>
                <ListItem>
                  <strong>Create Checklist</strong> — Name* · Active · Items (label, Required) · Add item · Save.
                </ListItem>
                <ListItem>
                  Used for field-engineer / visit checklists (separate from task Checklist Templates).
                </ListItem>
              </UnorderedList>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem borderColor={border}>
          <h3>
            <AccordionButton py={3} px={2}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                14. How catalog data is used (quick map)
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h3>
          <AccordionPanel pb={5} px={2}>
            <StepContent border={border} textSecondary={textSecondary}>
              <UnorderedList spacing={2} pl={1}>
                <ListItem>
                  <strong>Tickets form</strong> — Service Types, Issue Categories, Form Options (severity, source,
                  billing, hold reasons…), Tool / Part Catalog, Support Tiers (if enabled).
                </ListItem>
                <ListItem>
                  <strong>Ticket status</strong> — Parent Ticket Statuses; SLA Policies for response/resolution.
                </ListItem>
                <ListItem>
                  <strong>Tasks tab</strong> — Task Templates, Task Statuses, Checklist Templates, Task Field Modules.
                </ListItem>
                <ListItem>
                  <strong>Resolved tickets</strong> — CSAT Surveys invitations.
                </ListItem>
                <ListItem>
                  <strong>FE Visit</strong> — FE Checklist Builder; Task Statuses with FE Visit scope.
                </ListItem>
              </UnorderedList>
            </StepContent>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  )
}
